import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { publicAPI } from '@/lib/api';
import { coerceConfigBool } from '@/lib/coerceConfigBool';

interface ConfigContextType {
    voiceSearchEnabled: boolean;
    /** True once public config has loaded and voice search is enabled. */
    showVoiceSearch: boolean;
    /** True after the first successful public config fetch. */
    configReady: boolean;
    aiChatEnabled: boolean;
    propertyComparisonEnabled: boolean;
    aadhaarVerificationEnabled: boolean;
    /** When true, subscription/plans UI is hidden; only balance + Add credits (top-up) are shown. */
    creditsOnlyMode: boolean;
    internationalListingsEnabled: boolean;
    /** PostGIS map search + geo chat APIs enabled for this deployment. */
    geoSearchEnabled: boolean;
    /** When true, non-admin users see the maintenance page. True after fetch failure (fail closed). */
    maintenanceMode: boolean;
    maxPropertyMediaLimit: number;
    maxScraperVideoLimit: number;
    /** Max size in MB for a single uploaded property video (default 50). */
    maxVideoUploadMb: number;
    /** Max size in MB for a single uploaded property image (default 10). */
    maxImageUploadMb: number;
    googleMapsPremiumEnabled: boolean;
    mapsEnabled: boolean;
    mapsSearchMode: 'off' | 'openstreetmap' | 'google';
    mapsPostingMode: 'off' | 'openstreetmap' | 'google';
    googleMapsMapPriceUsd: number;
    googleMapsLookupPriceUsd: number;
    googleMapsMapPriceInr: number;
    googleMapsLookupPriceInr: number;
    googleMapsBlockWhenUnpaid: boolean;
    isLoading: boolean;
    refreshConfig: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ children }: { children: ReactNode }) {
    const [config, setConfig] = useState<{
        voiceSearchEnabled: boolean;
        aiChatEnabled: boolean;
        propertyComparisonEnabled: boolean;
        aadhaarVerificationEnabled: boolean;
        creditsOnlyMode: boolean;
        internationalListingsEnabled: boolean;
        geoSearchEnabled: boolean;
        maintenanceMode: boolean;
        maxPropertyMediaLimit: number;
        maxScraperVideoLimit: number;
        maxVideoUploadMb: number;
        maxImageUploadMb: number;
        googleMapsPremiumEnabled: boolean;
        mapsEnabled: boolean;
        mapsSearchMode: 'off' | 'openstreetmap' | 'google';
        mapsPostingMode: 'off' | 'openstreetmap' | 'google';
        googleMapsMapPriceUsd: number;
        googleMapsLookupPriceUsd: number;
        googleMapsMapPriceInr: number;
        googleMapsLookupPriceInr: number;
        googleMapsBlockWhenUnpaid: boolean;
    }>({
        voiceSearchEnabled: false,
        aiChatEnabled: true,
        propertyComparisonEnabled: true,
        aadhaarVerificationEnabled: false,
        creditsOnlyMode: true,
        internationalListingsEnabled: false,
        geoSearchEnabled: false,
        maintenanceMode: false,
        maxPropertyMediaLimit: 15,
        maxScraperVideoLimit: 2,
        maxVideoUploadMb: 50,
        maxImageUploadMb: 10,
        googleMapsPremiumEnabled: false,
        mapsEnabled: true,
        mapsSearchMode: 'openstreetmap',
        mapsPostingMode: 'openstreetmap',
        googleMapsMapPriceUsd: 0.01,
        googleMapsLookupPriceUsd: 0.03,
        googleMapsMapPriceInr: 1,
        googleMapsLookupPriceInr: 3,
        googleMapsBlockWhenUnpaid: true,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [configReady, setConfigReady] = useState(false);

    const fetchConfig = useCallback(async (options?: { force?: boolean }) => {
        const maxAttempts = options?.force ? 1 : 3;
        let lastError: unknown;
        try {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const data = await publicAPI.getPublicConfig({ force: options?.force || attempt > 1 });
                    setConfig({
                        voiceSearchEnabled: coerceConfigBool(data.voice_search_enabled, false),
                        aiChatEnabled: coerceConfigBool(data.ai_chat_enabled, true),
                        propertyComparisonEnabled: coerceConfigBool(data.property_comparison_enabled, true),
                        aadhaarVerificationEnabled: coerceConfigBool(data.aadhaar_verification_enabled, false),
                        creditsOnlyMode: data.credits_only_mode !== false,
                        internationalListingsEnabled: coerceConfigBool(data.international_listings_enabled, false),
                        geoSearchEnabled: coerceConfigBool(
                            data.geo_search_enabled,
                            import.meta.env.DEV,
                        ),
                        maintenanceMode: coerceConfigBool(data.maintenance_mode, false),
                        maxPropertyMediaLimit: typeof data.max_property_media_limit === 'number' && data.max_property_media_limit > 0 ? data.max_property_media_limit : 15,
                        maxScraperVideoLimit: typeof data.max_scraper_video_limit === 'number' && data.max_scraper_video_limit >= 0 ? data.max_scraper_video_limit : 2,
                        maxVideoUploadMb: typeof data.max_video_upload_mb === 'number' && data.max_video_upload_mb > 0 ? data.max_video_upload_mb : 50,
                        maxImageUploadMb: typeof data.max_image_upload_mb === 'number' && data.max_image_upload_mb > 0 ? data.max_image_upload_mb : 10,
                        googleMapsPremiumEnabled: coerceConfigBool(data.google_maps_premium_enabled, false),
                        mapsEnabled: data.maps_enabled !== false,
                        mapsSearchMode: data.maps_search_mode === 'off' || data.maps_search_mode === 'google' ? data.maps_search_mode : 'openstreetmap',
                        mapsPostingMode: data.maps_posting_mode === 'off' || data.maps_posting_mode === 'google' ? data.maps_posting_mode : 'openstreetmap',
                        googleMapsMapPriceUsd: typeof data.google_maps_map_price_usd === 'number' ? data.google_maps_map_price_usd : 0.01,
                        googleMapsLookupPriceUsd: typeof data.google_maps_lookup_price_usd === 'number' ? data.google_maps_lookup_price_usd : 0.03,
                        googleMapsMapPriceInr: typeof data.google_maps_map_price_inr === 'number' ? data.google_maps_map_price_inr : 1,
                        googleMapsLookupPriceInr: typeof data.google_maps_lookup_price_inr === 'number' ? data.google_maps_lookup_price_inr : 3,
                        googleMapsBlockWhenUnpaid: coerceConfigBool(data.google_maps_block_when_unpaid, true),
                    });
                    setConfigReady(true);
                    return;
                } catch (error) {
                    lastError = error;
                    if (attempt < maxAttempts) {
                        await new Promise((r) => setTimeout(r, 400 * attempt));
                    }
                }
            }
            console.error('Failed to fetch public config:', lastError);
            // Only fail-closed into maintenance on the initial load — not on background refreshes.
            if (!options?.force) {
                setConfig((prev) => ({
                    ...prev,
                    maintenanceMode: true,
                }));
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchConfig();
    }, [fetchConfig]);

    const showVoiceSearch = configReady && config.voiceSearchEnabled;

    return (
        <ConfigContext.Provider value={{
            ...config,
            showVoiceSearch,
            configReady,
            isLoading,
            refreshConfig: () => fetchConfig({ force: true }),
        }}>
            {children}
        </ConfigContext.Provider>
    );
}

const DEFAULT_FALLBACK_CONFIG: ConfigContextType = {
    voiceSearchEnabled: true,
    aiChatEnabled: true,
    propertyComparisonEnabled: true,
    aadhaarVerificationEnabled: false,
    creditsOnlyMode: true,
    internationalListingsEnabled: false,
    geoSearchEnabled: false,
    maintenanceMode: false,
    maxPropertyMediaLimit: 15,
    maxScraperVideoLimit: 2,
    maxVideoUploadMb: 50,
    maxImageUploadMb: 10,
    googleMapsPremiumEnabled: false,
    mapsEnabled: true,
    mapsSearchMode: 'openstreetmap',
    mapsPostingMode: 'openstreetmap',
    googleMapsMapPriceUsd: 0.01,
    googleMapsLookupPriceUsd: 0.03,
    googleMapsMapPriceInr: 1,
    googleMapsLookupPriceInr: 3,
    googleMapsBlockWhenUnpaid: true,
    showVoiceSearch: true,
    configReady: true,
    isLoading: false,
    refreshConfig: async () => {},
};

export function useConfig() {
    const context = useContext(ConfigContext);
    if (context === undefined) {
        return DEFAULT_FALLBACK_CONFIG;
    }
    return context;
}
